import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Check } from 'lucide-react';
import { BookmarkCard } from './BookmarkCard';
import type { BookmarkWithTags, Collection } from '@/types/database';
import type { DragData } from './DndProvider';

interface DraggableBookmarkCardProps {
  bookmark: BookmarkWithTags;
  collections: Collection[];
  onUpdate: () => void;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: () => void;
}

export function DraggableBookmarkCard({
  bookmark,
  collections,
  onUpdate,
  selectionMode = false,
  isSelected = false,
  onToggleSelection,
}: DraggableBookmarkCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: bookmark.id,
    data: {
      type: 'bookmark',
      bookmark,
    } satisfies DragData,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e: React.MouseEvent) => {
    if (selectionMode) {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelection?.();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/drag relative ${selectionMode ? 'cursor-pointer' : ''}`}
      onClick={handleClick}
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div
          className={`absolute left-2 top-2 z-20 flex h-5 w-5 items-center justify-center rounded border-2 transition-all ${
            isSelected
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-muted-foreground/50 bg-background hover:border-primary'
          }`}
        >
          {isSelected && <Check className="h-3 w-3" />}
        </div>
      )}

      {/* Drag handle - hidden in selection mode */}
      {!selectionMode && (
        <div
          {...listeners}
          {...attributes}
          className="absolute left-0 top-0 bottom-0 z-10 flex w-6 cursor-grab items-center justify-center rounded-l-lg bg-muted/50 opacity-0 transition-opacity hover:bg-muted group-hover/drag:opacity-100 active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      {/* Selection highlight */}
      {selectionMode && isSelected && (
        <div className="absolute inset-0 z-10 rounded-lg ring-2 ring-primary ring-offset-2 pointer-events-none" />
      )}

      <BookmarkCard
        bookmark={bookmark}
        collections={collections}
        onUpdate={onUpdate}
        disabled={selectionMode}
      />
    </div>
  );
}
